import React, { useState, useRef } from 'react'
import classnames from 'classnames'
import CancelIcon from '@Images/cancel-icon.svg'

import SpinnerIcon from './SpinnerIcon'
import UploadProgressBar from './UploadProgressBar'
import CheckCircleIcon from './CheckCircleIcon'
import UploadIcon from './UploadIcon'

import {
  UploadState
} from './QuickStart'

interface DragDropUploadZoneProps {
  readonly uploadState: UploadState
  readonly uploadProgress: number
  readonly fileName: string
  readonly fileSize: number
  readonly youtubeUrl: string
  onFileSelected(file: File): void
  onCancelUpload(): void
  setYoutubeUrl(newYoutubeUrl: string): void
}

const DragDropUploadZone: React.FC<DragDropUploadZoneProps> = (props) => {
  const { uploadState, uploadProgress, fileName, fileSize, onFileSelected, onCancelUpload, youtubeUrl, setYoutubeUrl } = props
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    const files = e.dataTransfer.files
    if (files && files.length > 0) {
      onFileSelected(files[0])
    }
  }

  const handleClick = () => {
    if (uploadState === 'idle') {
      fileInputRef.current?.click()
    }
  }

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      onFileSelected(files[0])
    }
  }

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setYoutubeUrl(e.target.value)
  }

  const handleUrlWrapperClick = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  const handleCancelClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onCancelUpload()
  }

  const truncateFileName = (name: string, maxLen: number) => {
    if (name.length <= maxLen) { return name }
    const ext = name.lastIndexOf('.')
    if (ext === -1) { return name.substring(0, maxLen - 3) + '...' }
    const extension = name.substring(ext)
    const baseName = name.substring(0, ext)
    const available = maxLen - extension.length - 3
    if (available <= 0) { return name.substring(0, maxLen - 3) + '...' }
    return baseName.substring(0, available) + '...' + extension
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) { return '0 B' }
    const units = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    const size = (bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)
    return `${size} ${units[i]}`
  }

  if (uploadState === 'uploading') {
    const truncatedName = truncateFileName(fileName, 28)
    return (
      <div className="QuickStart__upload-zone QuickStart__upload-zone-uploading">
        <div className="QuickStart__upload-state-icon">
          <SpinnerIcon />
        </div>
        <p className="QuickStart__upload-filename">{truncatedName}</p>
        <p className="QuickStart__upload-progress-text">Uploading... {uploadProgress}%</p>
        <UploadProgressBar percentage={uploadProgress} />
        <button
          className="QuickStart__upload-cancel-button"
          data-testid="quickstart-upload-cancel"
          onClick={handleCancelClick}
          type="button"
        >
          <CancelIcon />
          <span className="QuickStart__upload-cancel-text">Cancel</span>
        </button>
      </div>
    )
  }

  if (uploadState === 'success') {
    const truncatedName = truncateFileName(fileName, 28)
    const formattedSize = formatFileSize(fileSize)
    return (
      <div className="QuickStart__upload-zone QuickStart__upload-zone-success">
        <div className="QuickStart__upload-state-icon">
          <CheckCircleIcon />
        </div>
        <p className="QuickStart__upload-success-text">Upload complete!</p>
        <p className="QuickStart__upload-success-details">{truncatedName} · {formattedSize}</p>
      </div>
    )
  }

  return (
    <div
      data-testid="quickstart-media-dropzone"
      className={classnames(
        'QuickStart__upload-zone',
        { 'QuickStart__upload-zone-dragging': isDragging}
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="video/*"
        className="QuickStart__upload-file-input"
        data-testid="quickstart-media-input"
        onChange={handleFileInputChange}
      />
      <div className="QuickStart__upload-icon">
        <UploadIcon />
      </div>
      <p className="QuickStart__upload-text">Drag and drop your video here</p>
      <p className="QuickStart__upload-subtext">or click to browse files</p>
      <div className="QuickStart__upload-divider">
        <span className="QuickStart__upload-divider-line" />
        <span className="QuickStart__upload-divider-text">OR</span>
        <span className="QuickStart__upload-divider-line" />
      </div>
      <div className="QuickStart__upload-url-wrapper" onClick={handleUrlWrapperClick}>
        <input
          className="QuickStart__upload-url-input"
          type="text"
          placeholder="Paste a YouTube URL"
          data-testid="quickstart-media-url-input"
          value={youtubeUrl}
          onChange={handleUrlChange}
        />
      </div>
    </div>
  )
}

export default DragDropUploadZone
